import { Inject, Injectable } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import maplibregl, { SymbolLayerSpecification, VectorSourceSpecification } from 'maplibre-gl'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { Router } from '@angular/router'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from '../layers-config/layers'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { DataRolloutTilesService } from '../services/data-rollout-tiles-service'
import { ToastService } from '../services/toast.service'
import { formatDateToYYYYMMDD } from '../utils/date.utils'
import { withSignTypeFilter } from '../utils/property-filter.utils'

@Injectable()
export class LayerRolloutsFilteredMatchedSignsTileService {
  private layer = Layers.RolloutsFilteredMatchedSignsTile.name
  public layerSymbolsId = `${this.layer}-symbols`
  public layerSubsSymbolsId = `${this.layer}-subs-symbols`

  public sourceSymbolsId = `${this.layer}-symbol-source`
  public sourceSymbolsCountId = `${this.layer}-symbol-count-source`

  public rolloutsTileSourceId = 'static-filtered-matched-sign-rollouts-map-tile-source'
  public sourceRolloutFilteredMatchedSignsLayerName = 'filtered'

  public isLayerVisible = false
  public isRebuilding = false
  private cleanUp$ = new Subject<void>()
  private timestamp: Date | undefined
  public selectedFeatureId: string = ''
  public layerInitialized: boolean = false
  private currentDatasource: string = ''
  public onDestroy: boolean = false
  private minZoom = 7
  public whileReload = false

  private filteredMatchedSignsBaseFilter: maplibregl.ExpressionSpecification = ['all']
  private filteredMatchedSignsSubBaseFilter: maplibregl.ExpressionSpecification = [
    'all',
    ['>=', ['get', 'supplementalAmount'], 1],
  ]

  private rolloutsTileVectorSource: VectorSourceSpecification | null = null

  private filteredMatchedSignsLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSymbolsId,
    source: this.rolloutsTileSourceId,
    'source-layer': this.sourceRolloutFilteredMatchedSignsLayerName,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.filteredMatchedSignsBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-rotate': ['get', 'heading'],
      'icon-anchor': 'bottom',
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'case',
        ['==', ['get', 'signId'], ''],
        10000, // High value to bring selected feature to front
        0, // Use existing isFocused or default to 0
      ],
      'icon-allow-overlap': true,

      'icon-image': [
        'case',
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          [
            'case',
            ['==', ['get', 'speedLimit'], null],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_NULL_DIGITAL'],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ],
          ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit']],
        ],
        // Speed limit end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit']],
        ],

        // Speed limit zone signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit']],
        ],

        // Speed limit zone end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE_END, '_', ['get', 'speedLimit'], '_DIGITAL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE_END, '_', ['get', 'speedLimit']],
        ],

        // Simple icon mappings (no concatenation needed)
        ['==', ['get', 'mainType'], MapSignsIcons.ALL_RESTRICTIONS_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.ALL_RESTRICTIONS_END, '_DIGITAL'],
          MapSignsIcons.ALL_RESTRICTIONS_END,
        ],

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_START],
        MapSignsIcons.CITY_START,

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_END],
        MapSignsIcons.CITY_END,

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY],
        MapSignsIcons.HIGHWAY,

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY_END],
        MapSignsIcons.HIGHWAY_END,

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY],
        MapSignsIcons.MOTORWAY,

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY_END],
        MapSignsIcons.MOTORWAY_END,

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING],
        MapSignsIcons.TRAFFIC_CALMING,

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING_END],
        MapSignsIcons.TRAFFIC_CALMING_END,

        ['==', ['get', 'mainType'], MapSignsIcons.YIELD],
        MapSignsIcons.YIELD,

        // Default fallback for unknown types
        MapSignsIcons.UNKNOWN,
      ],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', ['==', ['id'], ''], 0.15, 0.12],
        7,
        ['case', ['==', ['id'], ''], 0.25, 0.2],
        13,
        ['case', ['==', ['id'], ''], 0.6, 0.5],
        18,
        ['case', ['==', ['id'], ''], 1, 0.8],
      ],
    },
    paint: {
      'icon-opacity': 1,
    },
  }

  private filteredMatchedSignsSubLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSubsSymbolsId,
    source: this.rolloutsTileSourceId,
    'source-layer': this.sourceRolloutFilteredMatchedSignsLayerName,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.filteredMatchedSignsSubBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-rotate': ['get', 'heading'],
      'icon-anchor': 'center',
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'case',
        ['==', ['get', 'signId'], ''],
        10000, // High value to bring selected feature to front
        0, // Use existing isFocused or default to 0
      ],
      'icon-allow-overlap': true,
      'icon-image': [
        'case',
        // Single supplemental sign - use different icon
        ['==', ['get', 'supplementalAmount'], 1],
        ['get', 'supplementalType'],

        // Multiple supplemental signs - use existing unknown icon
        ['concat', MapSupsIcons.SUP_UNKNOWN, '_', ['get', 'supplementalAmount']],
      ],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        5,
        ['case', ['==', ['id'], ''], 0.15, 0.12],
        7,
        ['case', ['==', ['id'], ''], 0.25, 0.2],
        13,
        ['case', ['==', ['id'], ''], 0.6, 0.5],
        18,
        ['case', ['==', ['id'], ''], 1, 0.8],
      ],
      'icon-offset': [0, 25],
    },
    paint: {
      'icon-opacity': 1,
    },
  }

  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    private dataRolloutTilesService: DataRolloutTilesService,
    private toastService: ToastService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {
    this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
    this.currentDatasource =
      this.appState.selectDatasource() ||
      this.appState.authService.getUserClaims().userDatasources[0] ||
      ''

    if (mapService.isMapReady) {
      this.updateLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.updateLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      // If a previous load failed (layerInitialized=false) but the timestamp or
      // datasource changed, retry loading the layer.
      if (!this.layerInitialized && this.appState.hasTimestampChanged()) {
        this.updateLayer()
        return
      }
      if (!this.layerInitialized && this.appState.hasDatasourceParamChanged()) {
        this.updateLayer()
        return
      }
      if (this.layerInitialized) {
        this.evaluateState()
      }
    })
  }
  private async selectVectorSource(timestamp: Date | undefined) {
    return await this.dataRolloutTilesService.getRolloutByTimestamp(
      timestamp,
      this.currentDatasource,
    )
  }

  private async updateLayer() {
    if (!this.currentDatasource) {
      this.rebuildForNewRollout()
      this.toastService.show({
        type: 'warning',
        message:
          'No datasources are available for your account. Please contact your administrator.',
        delay: 7000,
      })
      return
    }
    this.timestamp = this.appState.selectTimestamp()
    this.currentDatasource = this.appState.selectDatasource() || this.currentDatasource
    try {
      this.rolloutsTileVectorSource = await this.selectVectorSource(this.timestamp)
    } catch {
      this.rolloutsTileVectorSource = null
    }
    if (this.rolloutsTileVectorSource != null) {
      this.rebuildForNewRollout()
    } else {
      // Remove old layers so the user sees the screen is cleared
      this.rebuildForNewRollout()
      this.toastService.show({
        type: 'warning',
        message: `No rollouts available for the selected datasource${this.timestamp ? ` on ${formatDateToYYYYMMDD(this.timestamp)}` : ''}.`,
        delay: 5000,
      })
      return
    }

    // source is used in rollouts and matched signs layer, so we need to add it only once
    if (!this.mapService.map?.getSource(this.rolloutsTileSourceId)) {
      this.mapService.map?.addSource(this.rolloutsTileSourceId, {
        ...this.rolloutsTileVectorSource!,
      })
    }
    if (this.mapService.map.getLayer(`${Layers.RolloutsMatchedSignsTile.name}-subs-symbols`)) {
      this.mapService.map?.addLayer(
        this.filteredMatchedSignsSubLayerSpecification,
        `${Layers.RolloutsMatchedSignsTile.name}-subs-symbols`,
      )
      this.mapService.map?.addLayer(
        this.filteredMatchedSignsLayerSpecification,
        `${Layers.RolloutsMatchedSignsTile.name}-subs-symbols`,
      )
    } else {
      this.mapService.map.addLayer(this.filteredMatchedSignsSubLayerSpecification)
      this.mapService.map?.addLayer(this.filteredMatchedSignsLayerSpecification)
    }

    this.applySignTypeFilter()

    this.mapEventsService.registerForMapEvents({ layerId: this.layerSymbolsId }, true)

    this.layerInitialized = true

    // Immediately update the layer properties with the correct selectedFeatureId
    if (this.selectedFeatureId && this.selectedFeatureId !== '') {
      this.changeSelectAndHoverStateOfSelectedFeature()
    }

    // Check visibility based on current state
    await this.evaluateState()
  }

  public rebuildForNewRollout(): void {
    // Ensure the source exists
    const map = this.mapService.map
    if (!map) return

    // Detach events first
    this.mapEventsService.detachMapEvents({ layerId: this.layerSymbolsId })

    // Remove layers in specific order (reverse of how they were added)
    // Remove main layer first
    if (map.getLayer(this.layerSymbolsId)) {
      map.removeLayer(this.layerSymbolsId)
    }

    // Then remove sub layer
    if (map.getLayer(this.layerSubsSymbolsId)) {
      map.removeLayer(this.layerSubsSymbolsId)
    }

    // Remove source (can only be removed after ALL layers using it are removed)
    if (map.getSource(this.rolloutsTileSourceId)) {
      map.removeSource(this.rolloutsTileSourceId)
    }

    // Reset internal state
    this.layerInitialized = false
  }

  ngOnDestroy() {
    this.onDestroy = true
    this.mapEventsService.detachMapEvents({ layerId: this.layerSymbolsId })
    this.mapService.map.removeLayer(this.layerSubsSymbolsId)
    this.mapService.map.removeLayer(this.layerSymbolsId)

    this.cleanUp$.next()
  }

  private async evaluateState() {
    const layers = this.appState.selectLayers()
    if (layers.some((layer) => layer === Layers.RolloutsFilteredMatchedSignsTile.name)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }
    // if layer is not visible stop all events
    if (this.isLayerVisible === false) {
      return
    }

    this.applySignTypeFilter()

    if (this.appState.hasMapBoundsChanged() && !this.appState.getState().historyEnabled) {
    }

    if (
      this.appState.hasSelectedFeatureIdChanged() &&
      this.appState.getState().selectedFeatureId !== ''
    ) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
      this.changeSelectAndHoverStateOfSelectedFeature()
    }

    if (
      this.appState.selectDatasource() !== this.currentDatasource &&
      this.appState.hasDatasourceParamChanged()
    ) {
      this.currentDatasource = this.appState.selectDatasource() ?? ''
      this.updateLayer()
      return
    }

    // Compare with the loaded timestamp: hasTimestampChanged() misses changes made while the layer was hidden
    if (this.appState.selectTimestamp()?.getTime() !== this.timestamp?.getTime()) {
      this.updateLayer()
    }
  }

  private applySignTypeFilter() {
    const selectedSignTypes = this.appState.selectSignTypes()
    const propertyFilters = this.appState.selectPropertyFilters()

    if (this.mapService.map.getLayer(this.layerSymbolsId)) {
      this.mapService.map.setFilter(
        this.layerSymbolsId,
        withSignTypeFilter(this.filteredMatchedSignsBaseFilter, selectedSignTypes, propertyFilters),
      )
    }

    if (this.mapService.map.getLayer(this.layerSubsSymbolsId)) {
      this.mapService.map.setFilter(
        this.layerSubsSymbolsId,
        withSignTypeFilter(
          this.filteredMatchedSignsSubBaseFilter,
          selectedSignTypes,
          propertyFilters,
        ),
      )
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      this.mapService.setLayerVisibility(this.layerSymbolsId, isVisible)
      this.mapService.setLayerVisibility(this.layerSubsSymbolsId, isVisible)
    }
  }

  private changeSelectAndHoverStateOfSelectedFeature() {
    if (this.onDestroy) return
    this.mapService.map.setLayoutProperty(this.layerSymbolsId, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 0.15, 0.12],
      7,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 0.25, 0.2],
      13,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 0.6, 0.5],
      18,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 1.0, 0.8],
    ])

    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-size', [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 0.15, 0.12],
      7,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 0.25, 0.2],
      13,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 0.6, 0.5],
      18,
      ['case', ['==', ['get', 'signId'], this.selectedFeatureId], 1.0, 0.8],
    ])

    this.mapService.map.setLayoutProperty(this.layerSymbolsId, 'symbol-sort-key', [
      'case',
      ['==', ['get', 'signId'], this.selectedFeatureId],
      10000, // High value to bring selected feature to front
      0, // Use existing isFocused o
    ])

    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'symbol-sort-key', [
      'case',
      ['==', ['get', 'signId'], this.selectedFeatureId],
      10000, // High value to bring selected feature to front
      0, // Use existing isFocused o
    ])
    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-offset', [0, 25])
    this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'text-offset', [
      'case',
      ['==', ['get', 'signId'], this.selectedFeatureId],
      ['literal', [0, 1.45]], // Selected feature: text positioned above icon
      ['literal', [0, 1.2]], // Non-selected feature: text positioned on icon
    ])
  }
}
